import { useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { deleteUser } from "~/api/v2/requests";
import { useUserDocumentRoles } from "~/api/v2/useUserDocumentRoles";
import { Link, useNavigate, useParams } from "react-router";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import HeadingPage from "~/molecules/HeadingPage";
import useUser from "~/api/swr/useUser";
import AvatarWithFallback from "~/molecules/AvatarWithFallback";

export default function DeleteUserPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: roles, isLoading: isLoadingRoles } = useUserDocumentRoles(id);
  const { data: user, isLoading: isUserLoading } = useUser(id);

  if (id === undefined) {
    return null;
  }

  const handleDelete = async () => {
    try {
      setIsSubmitting(true);
      await deleteUser(id);
      void navigate("/users");
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingRoles || isUserLoading) {
    return <CenterSpinner />;
  }

  if (!roles || !user) {
    return <NoResult />;
  }

  if (roles.ownerIn.length === 0 && roles.publisherIn.length === 0) {
    return (
      <div className="container space-y-6 py-7">
        <HeadingPage title={t(($) => $.settings.userManagement.deleteUser)} />
        <div className="space-y-2">
          <p>{t(($) => $.settings.userManagement.deleteUserSupText)}</p>
          <Card className="inline-flex w-fit flex-row px-3 py-2">
            <AvatarWithFallback user={user} withName withEmail />
          </Card>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate("/users")}>
            {t(($) => $.commons.buttons.cancel)}
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isSubmitting}
          >
            {t(($) => $.settings.userManagement.deleteUser)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container space-y-2 py-7">
      <HeadingPage title={t(($) => $.settings.userManagement.deleteUser)} />
      <div>{t(($) => $.settings.userManagement.hasDocumentRoles)}</div>
      <div className="grid grid-cols-2">
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">
            {t(($) => $.commons.document.owner)}
          </h2>
          <ul>
            {roles.ownerIn.map((d) => (
              <li key={`owner-${d.documentId}`}>
                <Button asChild variant="link">
                  <Link to={`/documents/${d.documentId}`}>{d.title}</Link>
                </Button>
              </li>
            ))}
          </ul>
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold">
            {t(($) => $.commons.document.publisher)}
          </h2>
          <ul>
            {roles.publisherIn.map((d) => (
              <li key={`publisher-${d.documentId}`}>
                <Button asChild variant="link">
                  <Link to={`/documents/${d.documentId}`}>{d.title}</Link>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
